/**
 * Project save / open utilities.
 *
 * JSON structure
 * ──────────────
 * {
 *   "meta": { appName, appVersion, savedAt, filename },
 *   "inputs":  [ { variable, description, value, unit }, … ],
 *   "outputs": [ { variable, description, value, unit }, … ]
 * }
 *
 * • Load: read "inputs" (+ optional "gantt") — validated against the field
 *   definitions below and applied all-or-nothing (see parseProjectInputs).
 *   An input saved with `override: true` gets its "!" marker back.
 * • Export to docs: read both sections; every field is self-describing.
 */

import type { CalcInputs, CalcResult, VPFDimensions } from "@/types/calc";
import { REFRIGERANT_TYPES } from "@/lib/validators";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ProjectField {
  variable: string;
  description: string;
  value: string | number | boolean | null;
  unit: string;
  override?: boolean;     // inputs only: "!" soft-range override is set
}

export interface ProjectFile {
  meta: {
    appName: string;
    appVersion: string;
    savedAt: string;      // ISO-8601
    filename: string;
  };
  inputs: ProjectField[];
  outputs: ProjectField[];
  gantt?: ProjectField[];  // Gantt-specific scheduling inputs (optional for back-compat)
}

// ── Input field definitions ───────────────────────────────────────────────────

const INPUT_DEFS: { variable: keyof CalcInputs; description: string; unit: string }[] = [
  { variable: "productName",        description: "Product Name",                  unit: ""         },
  { variable: "thermalProperty",    description: "Thermal Properties",            unit: ""         },
  { variable: "inletTemp",          description: "Inlet Temperature",             unit: "°C"       },
  { variable: "outletTemp",         description: "Outlet Temperature",            unit: "°C"       },
  { variable: "density",            description: "Product Density",               unit: "kg/m³"    },
  { variable: "contactPc",          description: "Contact %",                     unit: "%"        },
  { variable: "refrigerantType",    description: "Refrigerant Type",              unit: ""         },
  { variable: "refrigerant",        description: "Refrigerant (Primary)",         unit: ""         },
  { variable: "evapTemp",           description: "Evaporation Temperature",       unit: "°C"       },
  { variable: "heatTransferFluid",  description: "Heat Transfer Fluid (Secondary)", unit: ""      },
  { variable: "htfInletTemp",       description: "HTF Inlet Temperature",         unit: "°C"       },
  { variable: "htfDeltaT",          description: "HTF Delta T",                   unit: "K"        },
  { variable: "nFreezers",          description: "No. of Freezers",               unit: "-"        },
  { variable: "nStations",          description: "No. of Stations",               unit: "-"        },
  { variable: "lStation",           description: "Station Length",                unit: "mm"       },
  { variable: "dStation",           description: "Station Depth",                 unit: "mm"       },
  { variable: "tStation",           description: "Station Thickness",             unit: "mm"       },
  { variable: "nDividers",          description: "No. of Dividers",               unit: "-"        },
  { variable: "loadingTime",        description: "Loading Time",                  unit: "sec/stn"  },
  { variable: "unloadingTime",      description: "Unloading Time",                unit: "sec/stn"  },
  { variable: "waitMisc",           description: "Wait / Misc",                   unit: "min"      },
  { variable: "cyclesWindowHrs",    description: "Cycles Window",                 unit: "hrs"      },
  { variable: "overrideFreezeTime", description: "Override Freeze Time",          unit: "min"      },
  { variable: "overrideDefrostMin", description: "Override Defrost Time",         unit: "min"      },
];

// ── Output field definitions ──────────────────────────────────────────────────

const RESULT_DEFS: { variable: string; description: string; unit: string }[] = [
  // Geometry / load
  { variable: "modelCode",           description: "Model Code",                     unit: ""         },
  { variable: "blockSize",           description: "Block Size",                     unit: "mm"       },
  { variable: "nPlates",             description: "No. of Plates",                  unit: "-"        },
  { variable: "tPlate",              description: "Plate Thickness",                unit: "mm"       },
  { variable: "lStackClosed",        description: "Stack Closed Length",            unit: "mm"       },
  { variable: "lStackOpen",          description: "Stack Open Length",              unit: "mm"       },
  { variable: "limitStackClosed",    description: "Stack Closed Limit",             unit: "mm"       },
  { variable: "estBlockWeight",      description: "Est. Block Weight",              unit: "kg"       },
  { variable: "blocksPerStation",    description: "Blocks per Station",             unit: "-"        },
  { variable: "estStationWeight",    description: "Est. Station Weight",            unit: "kg"       },
  { variable: "weightPerBatch",      description: "Weight per Batch",               unit: "kg"       },
  // Cycle times
  { variable: "closingTime",         description: "Time to Close / Lower",          unit: "sec"      },
  { variable: "loadingTime",         description: "Loading Time per Station",       unit: "sec/stn"  },
  { variable: "estFreezingTimeCalc", description: "Calculated Freeze Time",         unit: "min"      },
  { variable: "estFreezingTime",     description: "Est. Freezing Time (used)",      unit: "min"      },
  { variable: "defrosting",          description: "Defrosting",                     unit: "min"      },
  { variable: "openingTime",         description: "Time to Open / Raise",           unit: "sec"      },
  { variable: "unloading",           description: "Unloading Time per Station",     unit: "sec/stn"  },
  { variable: "waitMisc",            description: "Wait / Misc",                    unit: "min"      },
  { variable: "tLoad",               description: "Total Loading Time",             unit: "min"      },
  { variable: "tUnload",             description: "Total Unloading Time",           unit: "min"      },
  { variable: "tReload",             description: "Total Non-Freeze Time",          unit: "min"      },
  { variable: "tCycle",              description: "Total Cycle Time",               unit: "min"      },
  // Throughput
  { variable: "nominalCapacityEach",  description: "Nominal Capacity Each",         unit: "kg/hr"    },
  { variable: "nominalCapacityTotal", description: "Nominal Capacity Total",        unit: "kg/hr"    },
  { variable: "perDay24_7",           description: "Per Day 24/7",                  unit: "kg"       },
  { variable: "cyclesPer12Hrs",       description: "Cycles per 12 hrs",             unit: "-"        },
  { variable: "throughputPer12Hrs",   description: "Throughput per 12 hrs",         unit: "kg"       },
  { variable: "cyclesPer24Hrs",       description: "Cycles per 24 hrs",             unit: "-"        },
  { variable: "throughputPer24Hrs",   description: "Throughput per 24 hrs",         unit: "kg"       },
  { variable: "cyclesWindowHrs",      description: "Cycles Window",                 unit: "hrs"      },
  // Refrigeration
  { variable: "coolingLoadPerFreezer", description: "Cooling Load per Freezer",     unit: "kW"       },
  { variable: "overallCoolingLoad",    description: "Overall Cooling Load",         unit: "kW"       },
  { variable: "recirculationRate",     description: "Recirculation Rate",           unit: ":1"       },
  { variable: "maxDefrostCapacity",    description: "Max. Defrost Capacity",        unit: "kW"       },
  { variable: "peakCoolingLoad",       description: "Peak Cooling Load",            unit: "kW"       },
  { variable: "refMassFlow",           description: "Ref. Mass Flow / Fluid Mass Flow", unit: "kg/hr" },
  { variable: "refVolumeFlow",         description: "Ref. Volume Flow / Fluid Vol. Flow", unit: "L/hr" },
  { variable: "fluidCp",               description: "HTF Specific Heat",            unit: "kJ/(kg·K)" },
  { variable: "fluidRho",              description: "HTF Density",                  unit: "kg/m³"    },
  { variable: "orificeDiameter",       description: "Orifice Diameter",             unit: "mm"       },
  { variable: "orificeDP",             description: "Orifice dP",                   unit: "bar"      },
];

const VPF_DEFS: { variable: keyof VPFDimensions; description: string; unit: string }[] = [
  { variable: "OALength",         description: "Freezer Overall Length",   unit: "mm"  },
  { variable: "OADepth",          description: "Freezer Overall Depth",    unit: "mm"  },
  { variable: "OAHeightLowered",  description: "Freezer Height (Closed)",  unit: "mm"  },
  { variable: "OAHeightRaised",   description: "Freezer Height (Open)",    unit: "mm"  },
  { variable: "InternalVolume",   description: "Internal Volume",          unit: "L"   },
  { variable: "mPlates",          description: "Total Plate Mass",         unit: "kg"  },
  { variable: "pPressure",        description: "Product Pressure",         unit: "bar" },
];

// ── Gantt-own field definitions (scheduling inputs, not in CalcInputs) ───────

// Same key as in ganttTypes.ts — kept local to avoid a cross-lib import
const GANTT_KEY = "jackstone-gantt-inputs-v1";

const GANTT_DEFS: { variable: string; description: string; unit: string }[] = [
  { variable: "mFlow",             description: "Raw Product Flow",     unit: "kg/h"    },
  { variable: "tShift",            description: "Shift Length",         unit: "h"       },
  { variable: "startStockBatches", description: "Start Stock",          unit: "batches" },
  { variable: "goalT",             description: "Target Throughput",    unit: "t/shift" },
  { variable: "nCrews",            description: "No. of Crews",         unit: "-"       },
  { variable: "requireFullCycle",  description: "Require Full Cycle",   unit: ""        },
  { variable: "overtimeH",         description: "Unloading overtime (max)", unit: "h"   },
  { variable: "solveFor",          description: "Goal Seek: Solve For", unit: ""        },
];

// ── Build ─────────────────────────────────────────────────────────────────────

export function buildProjectFile(
  inputs: CalcInputs,
  r: CalcResult | null,
  vpf: VPFDimensions | null,
): ProjectFile {
  const now = new Date();
  const dateStr = now.toISOString().split("T")[0];           // YYYY-MM-DD
  const nFr    = (inputs.nFreezers || "?").replace(/!/g, "");
  const model  = r?.modelCode ?? "VPF";
  const filename = `${dateStr} - ${nFr}x - ${model}`;

  // ── Inputs ──
  // The "!" soft-range override is saved as `override: true` next to the
  // clean value, so the file stays numeric and the flag survives a reload.
  const inputFields: ProjectField[] = INPUT_DEFS
    .map(def => {
      const raw = inputs[def.variable] ?? "";
      if (raw === "") return null;
      const stripped = String(raw).replace(/!/g, "").trim();
      const num = Number(stripped);
      const value: ProjectField["value"] = (stripped !== "" && !isNaN(num)) ? num : stripped || null;
      if (value === null) return null;
      const field: ProjectField = { variable: def.variable, description: def.description, value, unit: def.unit };
      if (String(raw).includes("!")) field.override = true;
      return field;
    })
    .filter(Boolean) as ProjectField[];

  // ── Outputs — CalcResult ──
  const outputFields: ProjectField[] = [];

  if (r) {
    RESULT_DEFS.forEach(def => {
      const val = (r as any)[def.variable];
      if (val !== null && val !== undefined) {
        outputFields.push({ variable: def.variable, description: def.description, value: val, unit: def.unit });
      }
    });
  }

  // ── Outputs — VPF geometry ──
  if (vpf) {
    VPF_DEFS.forEach(def => {
      const val = vpf[def.variable];
      if (val !== null && val !== undefined) {
        outputFields.push({ variable: def.variable, description: def.description, value: val as number, unit: def.unit });
      }
    });
  }

  // ── Gantt scheduling inputs (read from localStorage) ──
  const ganttFields: ProjectField[] = [];
  try {
    const raw = localStorage.getItem(GANTT_KEY);
    const ganttOwn = raw ? JSON.parse(raw) : {};
    GANTT_DEFS.forEach(def => {
      // The shift length is the Calculator's cyclesWindowHrs — write the same
      // value here (kept for older readers) rather than any stale Gantt copy.
      const val = def.variable === "tShift"
        ? (() => {
            const s = String(inputs.cyclesWindowHrs ?? "").replace(/!/g, "").trim();
            return s === "" ? null : (isFinite(Number(s)) ? Number(s) : s);
          })()
        : ganttOwn[def.variable];
      if (val !== null && val !== undefined) {
        ganttFields.push({
          variable: def.variable,
          description: def.description,
          value: val,
          unit: def.unit,
        });
      }
    });
  } catch {/* ignore — Gantt section is optional */}

  return {
    meta: { appName: "Jackstone VPF Freezer Sizing Calculator", appVersion: "1.0",
            savedAt: now.toISOString(), filename },
    inputs: inputFields,
    outputs: outputFields,
    gantt: ganttFields,
  };
}

// ── Download ──────────────────────────────────────────────────────────────────

export function triggerDownload(project: ProjectFile): void {
  const json = JSON.stringify(project, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${project.meta.filename}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Parse (restore inputs + gantt scheduling fields) ─────────────────────────

/** Reference lists the loaded values are checked against (when available). */
export interface ProjectRefLists {
  products?: string[];               // product (thermal property) names
  primaryRefrigerants?: string[];    // Primary refrigerant labels
  secondaryRefrigerants?: string[];  // Secondary heat-transfer-fluid labels
}

// Free-text / enum Calculator inputs — everything else must be numeric
const CALC_TEXT_KEYS = new Set<string>([
  "productName", "thermalProperty", "refrigerantType", "refrigerant", "heatTransferFluid",
]);
// MAN overrides — blank = AUTO is allowed
const CALC_BLANK_OK = new Set<string>(["overrideFreezeTime", "overrideDefrostMin"]);
const SOLVE_FOR = ["flow", "crews", "flow+crews"];

const isNumeric = (s: string) => {
  const c = s.replace(/!/g, "").trim();
  return c !== "" && isFinite(Number(c));
};

/** A field's value as a string; undefined for null / missing; throws on objects. */
function fieldString(field: ProjectField, where: string): string | undefined {
  const v = field.value;
  if (v === null || v === undefined) return undefined;
  if (typeof v === "string") return v;
  if ((typeof v === "number" && isFinite(v)) || typeof v === "boolean") return String(v);
  throw new Error(`${where} "${field.variable}" has an invalid value.`);
}

/**
 * Validate a project file and return the inputs to restore. Unknown variables
 * are ignored; malformed values, unknown enum values and products /
 * refrigerants missing from the loaded lists make the WHOLE file invalid —
 * nothing is applied (the Gantt section included) and the Error lists why.
 */
export function parseProjectInputs(json: string, refs: ProjectRefLists = {}): Partial<CalcInputs> {
  let project: ProjectFile;
  try {
    project = JSON.parse(json);
  } catch {
    throw new Error("The file is not valid JSON.");
  }
  if (!project || typeof project !== "object" || !Array.isArray(project.inputs)) {
    throw new Error("The file has no 'inputs' list — is it a Jackstone project file?");
  }

  const problems: string[] = [];
  const known = new Set<string>(INPUT_DEFS.map(d => d.variable));

  // ── Calculator inputs ──
  const partial: Partial<CalcInputs> = {};
  project.inputs.forEach((field) => {
    if (!field || typeof field !== "object" || typeof field.variable !== "string") return;
    if (!known.has(field.variable)) return;                 // unknown key — ignore
    let s: string | undefined;
    try { s = fieldString(field, "Input"); }
    catch (e: any) { problems.push(e.message); return; }
    if (s === undefined) return;
    s = s.trim();
    if (!CALC_TEXT_KEYS.has(field.variable)) {
      if (s === "" && CALC_BLANK_OK.has(field.variable)) { /* AUTO */ }
      else if (!isNumeric(s)) { problems.push(`"${field.variable}" must be a number (got "${s}").`); return; }
      // Restore the sticky "!" soft-range override
      if (field.override === true && s !== "" && !s.includes("!")) s = `${s}!`;
    }
    (partial as any)[field.variable] = s;
  });

  // ── Enum + reference-list checks ──
  if (partial.refrigerantType !== undefined
      && !(REFRIGERANT_TYPES as readonly string[]).includes(partial.refrigerantType)) {
    problems.push(`Refrigerant type must be Primary or Secondary (got "${partial.refrigerantType}").`);
  }
  const inList = (list: string[] | undefined, v: string | undefined) =>
    v === undefined || !list || list.length === 0 || list.includes(v);
  if (!inList(refs.products, partial.thermalProperty)) {
    problems.push(`Unknown product "${partial.thermalProperty}".`);
  }
  if (!inList(refs.primaryRefrigerants, partial.refrigerant)) {
    problems.push(`Unknown refrigerant "${partial.refrigerant}".`);
  }
  if (!inList(refs.secondaryRefrigerants, partial.heatTransferFluid)) {
    problems.push(`Unknown heat transfer fluid "${partial.heatTransferFluid}".`);
  }

  // ── Gantt scheduling inputs (optional section) ──
  const gantt: Record<string, string | boolean> = {};
  if (project.gantt !== undefined && !Array.isArray(project.gantt)) {
    problems.push("The 'gantt' section must be a list.");
  } else if (Array.isArray(project.gantt)) {
    const ganttKnown = new Set(GANTT_DEFS.map(d => d.variable));
    project.gantt.forEach((field) => {
      if (!field || typeof field !== "object" || typeof field.variable !== "string") return;
      if (!ganttKnown.has(field.variable)) return;          // unknown key — ignore
      let s: string | undefined;
      try { s = fieldString(field, "Gantt input"); }
      catch (e: any) { problems.push(e.message); return; }
      if (s === undefined) return;
      s = s.trim();
      if (field.variable === "requireFullCycle") {
        if (s !== "true" && s !== "false") problems.push(`"requireFullCycle" must be true or false.`);
        else gantt.requireFullCycle = s === "true";
      } else if (field.variable === "solveFor") {
        if (!SOLVE_FOR.includes(s)) problems.push(`"solveFor" must be one of ${SOLVE_FOR.join(", ")}.`);
        else gantt.solveFor = s;
      } else if (!isNumeric(s)) {
        problems.push(`Gantt "${field.variable}" must be a number (got "${s}").`);
      } else {
        gantt[field.variable] = s;
      }
    });
  }

  if (problems.length) {
    const more = problems.length > 3 ? ` (+${problems.length - 3} more)` : "";
    throw new Error(problems.slice(0, 3).join(" ") + more);
  }

  // Shift length: the Calculator's cyclesWindowHrs wins; an older file that
  // only carries the Gantt tShift supplies it. Never stored under GANTT_KEY.
  if (typeof gantt.tShift === "string") {
    if (partial.cyclesWindowHrs === undefined) partial.cyclesWindowHrs = gantt.tShift;
    delete gantt.tShift;
  }

  // Valid — now (and only now) restore the Gantt inputs into their own key
  if (Object.keys(gantt).length > 0) {
    try {
      let existing: Record<string, unknown> = {};
      try {
        const r = localStorage.getItem(GANTT_KEY);
        const parsed = r ? JSON.parse(r) : {};
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) existing = parsed;
      } catch {/* corrupt — start fresh */}
      localStorage.setItem(GANTT_KEY, JSON.stringify({ ...existing, ...gantt }));
    } catch {/* storage unavailable — ignore */}
  }

  return partial;
}
